import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  IconButton,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  Tabs,
  Tab,
  Snackbar,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Undo as UndoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Code as CodeIcon,
} from "@mui/icons-material";
import ConfirmDialog from "../ConfirmDialog";
import {
  fetchDeploymentsBySite,
  triggerDeployment,
  fetchGitConfigsBySite,
  rollbackDeployment,
  runDeploymentPrecheck,
  fetchDeploymentQueue,
} from "../../redux/slices/gitSlice";
import {
  fetchDeploymentLogs,
  cancelDeployment,
  retryDeployment,
  scheduleDeployment,
} from "../../redux/slices/deploymentSlice";
import useDeploymentSocket from "../../hooks/useDeploymentSocket";

const SiteDeploymentsTab = ({ siteId }) => {
  const dispatch = useDispatch();
  const [socketMessage, setSocketMessage] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // Use deployment socket for real-time updates
  const socketConnected = useDeploymentSocket(siteId, (message) => {
    setSocketMessage(message);
    setTimeout(() => setSocketMessage(""), 5000);
  });
  const {
    deployments,
    deploymentsLoading,
    gitConfigs,
    precheckResult,
    precheckLoading,
    deploymentQueue,
  } = useSelector((state) => state.git);
  const { deploymentLogs, logsLoading } = useSelector(
    (state) => state.deployment,
  );

  const [expandedDeployment, setExpandedDeployment] = useState(null);
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [_selectedLogs, setSelectedLogs] = useState(null);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [rollbackDialogOpen, setRollbackDialogOpen] = useState(false);
  const [selectedRollbackId, setSelectedRollbackId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);

  const [deployForm, setDeployForm] = useState({
    commitHash: "",
    branch: "",
    notes: "",
  });

  const [scheduleForm, setScheduleForm] = useState({
    scheduledFor: "",
    branch: "",
    notes: "",
  });

  useEffect(() => {
    if (siteId) {
      dispatch(fetchGitConfigsBySite(siteId));
      dispatch(fetchDeploymentsBySite({ siteId, params: { limit: 50 } }));
      dispatch(fetchDeploymentQueue(siteId));
    }
  }, [dispatch, siteId]);

  const handleRefresh = async () => {
    try {
      await Promise.all([
        dispatch(
          fetchDeploymentsBySite({ siteId, params: { limit: 50 } }),
        ).unwrap(),
        dispatch(fetchDeploymentQueue(siteId)).unwrap(),
      ]);
      setSnackbar({
        open: true,
        message: "Deployment history refreshed",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Failed to refresh deployment history",
        severity: "error",
      });
    }
  };

  const handleExpandDeployment = (deploymentId) => {
    setExpandedDeployment(
      expandedDeployment === deploymentId ? null : deploymentId,
    );
  };

  const handleOpenDeployDialog = (configId = null) => {
    if (configId) {
      const config = gitConfigs.find((c) => c.id === configId);
      setDeployForm({
        commitHash: "",
        branch: config?.branch || "main",
        notes: "",
      });
      setSelectedConfig(configId);
    } else if (gitConfigs && gitConfigs.length > 0) {
      setSelectedConfig(gitConfigs[0].id);
      setDeployForm({
        commitHash: "",
        branch: gitConfigs[0].branch,
        notes: "",
      });
    }
    setDeployDialogOpen(true);
  };

  const handleTriggerDeployment = async () => {
    if (!selectedConfig) return;

    try {
      await dispatch(
        triggerDeployment({
          configId: selectedConfig,
          payload: {
            branch: deployForm.branch,
            commitHash: deployForm.commitHash || undefined,
            notes: deployForm.notes || undefined,
          },
        }),
      ).unwrap();

      setDeployDialogOpen(false);

      // Show success message
      setSnackbar({
        open: true,
        message: "Deployment started! Watch the progress below.",
        severity: "success",
      });

      // Immediately fetch deployments to show the new deployment in PENDING state
      dispatch(fetchDeploymentsBySite({ siteId, params: { limit: 50 } }));
      dispatch(fetchDeploymentQueue(siteId));
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to start deployment: ${
          error.message || "Unknown error"
        }`,
        severity: "error",
      });
    }
  };

  const handleRunPrecheck = async () => {
    if (selectedConfig) {
      await dispatch(runDeploymentPrecheck(selectedConfig));
    }
  };

  const handleRollback = (deploymentId) => {
    setSelectedRollbackId(deploymentId);
    setRollbackDialogOpen(true);
  };

  const handleConfirmRollback = async () => {
    if (!selectedRollbackId) return;

    setRollbackDialogOpen(false);
    try {
      await dispatch(rollbackDeployment(selectedRollbackId)).unwrap();
      setSnackbar({
        open: true,
        message: "Rollback initiated successfully!",
        severity: "success",
      });
      setTimeout(() => {
        dispatch(fetchDeploymentsBySite({ siteId, params: { limit: 50 } }));
      }, 1000);
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Rollback failed: ${error.message || "Unknown error"}`,
        severity: "error",
      });
    } finally {
      setSelectedRollbackId(null);
    }
  };

  const handleCancelDeployment = async (deploymentId) => {
    setCancelTarget(deploymentId);
  };

  const confirmCancelDeployment = async () => {
    if (!cancelTarget) return;
    try {
      await dispatch(cancelDeployment(cancelTarget)).unwrap();
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to cancel deployment: ${error.message || error}`,
        severity: "error",
      });
    }
    setCancelTarget(null);
  };

  const handleRetryDeployment = async (deploymentId) => {
    try {
      await dispatch(retryDeployment(deploymentId)).unwrap();
      setTimeout(() => {
        dispatch(fetchDeploymentsBySite({ siteId, params: { limit: 50 } }));
      }, 1000);
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to retry deployment: ${error.message || error}`,
        severity: "error",
      });
    }
  };

  const handleViewLogs = async (deploymentId) => {
    try {
      const result = await dispatch(
        fetchDeploymentLogs({ deploymentId, includeDiff: true }),
      ).unwrap();
      setSelectedLogs(result);
      setLogsDialogOpen(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to load logs: ${
          error.message || "Logs not available"
        }`,
        severity: "error",
      });
    }
  };

  const handleScheduleDeployment = async () => {
    if (!selectedConfig) return;

    try {
      await dispatch(
        scheduleDeployment({
          configId: selectedConfig,
          scheduledFor: scheduleForm.scheduledFor,
          branch: scheduleForm.branch,
          notes: scheduleForm.notes || undefined,
        }),
      ).unwrap();

      setScheduleDialogOpen(false);
      setScheduleForm({ scheduledFor: "", branch: "", notes: "" });
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Failed to schedule deployment: ${error.message || error}`,
        severity: "error",
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "SUCCESS":
        return <CheckCircleIcon color="success" />;
      case "FAILED":
        return <ErrorIcon color="error" />;
      case "PENDING":
      case "QUEUED":
        return <HourglassEmptyIcon color="warning" />;
      case "CLONING":
      case "BUILDING":
      case "DEPLOYING":
        return <CircularProgress size={20} />;
      default:
        return <HourglassEmptyIcon />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "SUCCESS":
        return "success";
      case "FAILED":
        return "error";
      case "PENDING":
      case "QUEUED":
        return "warning";
      case "CLONING":
      case "BUILDING":
      case "DEPLOYING":
        return "info";
      case "CANCELLED":
        return "default";
      default:
        return "default";
    }
  };

  const formatDuration = (startTime, endTime) => {
    if (!startTime) return "N/A";
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const durationMs = end - start;
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  if (deploymentsLoading && !deployments?.length) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <CardContent>
      {/* Socket Message */}
      {socketMessage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {socketMessage}
        </Alert>
      )}

      {/* Header */}
      <Box mb={3}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Box>
            <Typography variant="h6">Deployments</Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {deploymentQueue && deploymentQueue.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  {deploymentQueue.length} deployment(s) in queue
                </Typography>
              )}
              {socketConnected && (
                <Chip
                  icon={<span>●</span>}
                  label="Live"
                  size="small"
                  color="success"
                />
              )}
            </Box>
          </Box>
          <Box>
            <Button
              variant="outlined"
              startIcon={<ScheduleIcon />}
              onClick={() => setScheduleDialogOpen(true)}
              sx={{ mr: 1 }}
              disabled={!gitConfigs || gitConfigs.length === 0}
            >
              Schedule
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              sx={{ mr: 1 }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={() => handleOpenDeployDialog()}
              disabled={!gitConfigs || gitConfigs.length === 0}
            >
              Deploy Now
            </Button>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Deploy code from your Git repository. View deployment history,
          rollback to previous versions, and monitor real-time progress.
        </Typography>
      </Box>

      {/* Tabs */}
      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
        <Tab label="History" />
        <Tab
          label={`Queue (${deploymentQueue?.length || 0})`}
          disabled={!deploymentQueue || deploymentQueue.length === 0}
        />
      </Tabs>

      {/* No Git Config Warning */}
      {(!gitConfigs || gitConfigs.length === 0) && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Git Repository Required
            </Typography>
            <Typography variant="body2">
              You need to connect a Git repository before you can deploy. Go to
              the <strong>Git</strong> tab to add your repository.
            </Typography>
          </Alert>
          {(!deployments || deployments.length === 0) &&
            !deploymentsLoading && (
              <Card sx={{ bgcolor: "background.paper" }}>
                <CardContent sx={{ textAlign: "center", py: 6 }}>
                  <PlayArrowIcon
                    sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
                  />
                  <Typography variant="h6" gutterBottom>
                    No Deployments Yet
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ maxWidth: 500, mx: "auto", mb: 2 }}
                  >
                    Once you connect a Git repository, you'll be able to deploy
                    your code with a single click. All deployment history will
                    appear here.
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PlayArrowIcon />}
                    onClick={handleOpenDeployDialog}
                    disabled={!gitConfigs || gitConfigs.length === 0}
                    sx={{ mt: 2 }}
                  >
                    Start First Deployment
                  </Button>
                </CardContent>
              </Card>
            )}
        </Box>
      )}

      {/* Deployments Table */}
      {tabValue === 0 && (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width="50px"></TableCell>
                <TableCell>
                  <Tooltip title="SUCCESS: Deployed successfully | FAILED: Deployment failed | PENDING: Waiting to start | CLONING/BUILDING/DEPLOYING: In progress">
                    <span>Status</span>
                  </Tooltip>
                </TableCell>
                <TableCell>Branch</TableCell>
                <TableCell>Commit</TableCell>
                <TableCell>Environment</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deployments && deployments.length > 0 ? (
                deployments.map((deployment) => (
                  <React.Fragment key={deployment.id}>
                    <TableRow>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => handleExpandDeployment(deployment.id)}
                        >
                          {expandedDeployment === deployment.id ? (
                            <ExpandLessIcon />
                          ) : (
                            <ExpandMoreIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getStatusIcon(deployment.status)}
                          <Chip
                            label={deployment.status}
                            size="small"
                            color={getStatusColor(deployment.status)}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={deployment.branch} size="small" />
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={deployment.commitHash || "No commit hash"}
                        >
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            sx={{
                              cursor: deployment.commitHash
                                ? "help"
                                : "default",
                            }}
                          >
                            {deployment.commitHash?.substring(0, 7) || "N/A"}
                          </Typography>
                        </Tooltip>
                        {deployment.commitMessage && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            {deployment.commitMessage.substring(0, 30)}
                            {deployment.commitMessage.length > 30 ? "..." : ""}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            deployment.gitConfig?.environment ||
                            deployment.environment
                          }
                          size="small"
                          color={
                            deployment.gitConfig?.environment === "PRODUCTION"
                              ? "error"
                              : deployment.gitConfig?.environment === "STAGING"
                                ? "warning"
                                : "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {formatDuration(
                          deployment.startedAt,
                          deployment.completedAt,
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {deployment.startedAt
                            ? new Date(deployment.startedAt).toLocaleString()
                            : "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={0.5}>
                          {deployment.status === "SUCCESS" && (
                            <Tooltip title="Rollback">
                              <IconButton
                                size="small"
                                onClick={() => handleRollback(deployment.id)}
                              >
                                <UndoIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {deployment.status === "FAILED" && (
                            <Tooltip title="Retry">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleRetryDeployment(deployment.id)
                                }
                              >
                                <RefreshIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {[
                            "PENDING",
                            "CLONING",
                            "BUILDING",
                            "DEPLOYING",
                          ].includes(deployment.status) && (
                            <Tooltip title="Cancel">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleCancelDeployment(deployment.id)
                                }
                              >
                                <StopIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="View Logs">
                            <IconButton
                              size="small"
                              onClick={() => handleViewLogs(deployment.id)}
                            >
                              <CodeIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        style={{ paddingBottom: 0, paddingTop: 0 }}
                      >
                        <Collapse
                          in={expandedDeployment === deployment.id}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box sx={{ m: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Details
                            </Typography>
                            <Typography variant="body2">
                              <strong>Commit Message:</strong>{" "}
                              {deployment.commitMessage || "N/A"}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Triggered By:</strong>{" "}
                              {deployment.triggerType}
                            </Typography>
                            {deployment.notes && (
                              <Typography variant="body2">
                                <strong>Notes:</strong> {deployment.notes}
                              </Typography>
                            )}
                            {deployment.errorMessage && (
                              <Alert severity="error" sx={{ mt: 1 }}>
                                {deployment.errorMessage}
                              </Alert>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary">
                      No deployments yet
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Queue Tab */}
      {tabValue === 1 && deploymentQueue && deploymentQueue.length > 0 && (
        <Box mt={2}>
          {deploymentQueue.map((item, index) => (
            <Card key={index} sx={{ mb: 1 }}>
              <CardContent>
                <Typography variant="body1">
                  Position: {index + 1} - Config ID: {item.configId}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Estimated wait: ~{index * 30} seconds
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Deploy Dialog */}
      <Dialog
        open={deployDialogOpen}
        onClose={() => setDeployDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Trigger Deployment</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Git Configuration</InputLabel>
              <Select
                value={selectedConfig}
                onChange={(e) => {
                  setSelectedConfig(e.target.value);
                  const config = gitConfigs.find(
                    (c) => c.id === e.target.value,
                  );
                  setDeployForm((prev) => ({
                    ...prev,
                    branch: config?.branch || "main",
                  }));
                }}
                label="Git Configuration"
              >
                {gitConfigs?.map((config) => (
                  <MenuItem key={config.id} value={config.id}>
                    {config.repositoryUrl.split("/").pop().replace(".git", "")}{" "}
                    ({config.environment})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Branch"
              value={deployForm.branch}
              onChange={(e) =>
                setDeployForm((prev) => ({ ...prev, branch: e.target.value }))
              }
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Commit Hash (Optional)"
              value={deployForm.commitHash}
              onChange={(e) =>
                setDeployForm((prev) => ({
                  ...prev,
                  commitHash: e.target.value,
                }))
              }
              placeholder="Leave empty for latest commit"
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Notes (Optional)"
              value={deployForm.notes}
              onChange={(e) =>
                setDeployForm((prev) => ({ ...prev, notes: e.target.value }))
              }
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />

            {precheckResult && (
              <Alert
                severity={precheckResult.success ? "success" : "error"}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2">
                  {precheckResult.message || "Pre-check completed"}
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeployDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRunPrecheck}
            disabled={!selectedConfig || precheckLoading}
          >
            Run Pre-check
          </Button>
          <Button
            onClick={handleTriggerDeployment}
            variant="contained"
            disabled={!selectedConfig || deploymentsLoading}
          >
            Deploy
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Schedule Deployment</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Git Configuration</InputLabel>
              <Select
                value={selectedConfig}
                onChange={(e) => setSelectedConfig(e.target.value)}
                label="Git Configuration"
              >
                {gitConfigs?.map((config) => (
                  <MenuItem key={config.id} value={config.id}>
                    {config.repositoryUrl.split("/").pop().replace(".git", "")}{" "}
                    ({config.environment})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Scheduled Time"
              type="datetime-local"
              value={scheduleForm.scheduledFor}
              onChange={(e) =>
                setScheduleForm((prev) => ({
                  ...prev,
                  scheduledFor: e.target.value,
                }))
              }
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Branch"
              value={scheduleForm.branch}
              onChange={(e) =>
                setScheduleForm((prev) => ({ ...prev, branch: e.target.value }))
              }
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Notes (Optional)"
              value={scheduleForm.notes}
              onChange={(e) =>
                setScheduleForm((prev) => ({ ...prev, notes: e.target.value }))
              }
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleScheduleDeployment}
            variant="contained"
            disabled={
              !selectedConfig ||
              !scheduleForm.scheduledFor ||
              !scheduleForm.branch
            }
          >
            Schedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* Logs Dialog - GitHub Style */}
      <Dialog
        open={logsDialogOpen}
        onClose={() => setLogsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CodeIcon />
            <Typography variant="h6">Deployment Details</Typography>
            {deploymentLogs?.commitHash && (
              <Chip
                label={deploymentLogs.commitHash.substring(0, 7)}
                size="small"
                sx={{ ml: 1, fontFamily: "monospace" }}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {logsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* Commit Info */}
              {deploymentLogs?.commitMessage && (
                <Paper
                  variant="outlined"
                  sx={{ p: 2, mb: 2, bgcolor: "action.hover" }}
                >
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Commit Message
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                    {deploymentLogs.commitMessage}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    by {deploymentLogs.commitAuthor} on branch{" "}
                    {deploymentLogs.branch}
                  </Typography>
                </Paper>
              )}

              {/* File Changes - GitHub Style */}
              {deploymentLogs?.fileChanges && (
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Typography variant="h6">
                      Changed Files (
                      {deploymentLogs.fileChanges.stats.changedFiles})
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Chip
                        label={`+${deploymentLogs.fileChanges.stats.additions}`}
                        size="small"
                        sx={{
                          bgcolor: "success.dark",
                          color: "success.contrastText",
                          fontWeight: 600,
                        }}
                      />
                      <Chip
                        label={`-${deploymentLogs.fileChanges.stats.deletions}`}
                        size="small"
                        sx={{
                          bgcolor: "error.dark",
                          color: "error.contrastText",
                          fontWeight: 600,
                        }}
                      />
                    </Box>
                  </Box>

                  <Paper variant="outlined" sx={{ overflow: "hidden" }}>
                    {deploymentLogs.fileChanges.files.map((file, index) => (
                      <React.Fragment key={index}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            p: 1.5,
                            borderBottom: 1,
                            borderColor: "divider",
                            "&:hover": { bgcolor: "action.hover" },
                            cursor: file.diff ? "pointer" : "default",
                          }}
                          onClick={() =>
                            file.diff &&
                            setExpandedDeployment(
                              expandedDeployment === file.filename
                                ? null
                                : file.filename,
                            )
                          }
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              flex: 1,
                            }}
                          >
                            {file.diff && (
                              <IconButton size="small" sx={{ p: 0 }}>
                                {expandedDeployment === file.filename ? (
                                  <ExpandLessIcon fontSize="small" />
                                ) : (
                                  <ExpandMoreIcon fontSize="small" />
                                )}
                              </IconButton>
                            )}
                            <Chip
                              label={
                                file.status === "added"
                                  ? "A"
                                  : file.status === "modified"
                                    ? "M"
                                    : "D"
                              }
                              size="small"
                              sx={{
                                minWidth: 24,
                                height: 20,
                                fontSize: "11px",
                                fontWeight: 600,
                                bgcolor:
                                  file.status === "added"
                                    ? "success.main"
                                    : file.status === "deleted"
                                      ? "error.main"
                                      : "warning.main",
                                color:
                                  file.status === "added"
                                    ? "success.contrastText"
                                    : file.status === "deleted"
                                      ? "error.contrastText"
                                      : "warning.contrastText",
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "13px",
                                color:
                                  file.status === "deleted"
                                    ? "text.disabled"
                                    : "inherit",
                                textDecoration:
                                  file.status === "deleted"
                                    ? "line-through"
                                    : "none",
                              }}
                            >
                              {file.filename}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                            }}
                          >
                            {file.additions > 0 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "success.light",
                                  fontWeight: 600,
                                  fontFamily: "monospace",
                                }}
                              >
                                +{file.additions}
                              </Typography>
                            )}
                            {file.deletions > 0 && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "error.light",
                                  fontWeight: 600,
                                  fontFamily: "monospace",
                                }}
                              >
                                -{file.deletions}
                              </Typography>
                            )}
                            <Box
                              sx={{
                                width: 60,
                                height: 6,
                                bgcolor: "action.disabledBackground",
                                borderRadius: 1,
                                overflow: "hidden",
                                display: "flex",
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${
                                    (file.additions /
                                      (file.additions + file.deletions)) *
                                    100
                                  }%`,
                                  bgcolor: "success.main",
                                }}
                              />
                              <Box
                                sx={{
                                  width: `${
                                    (file.deletions /
                                      (file.additions + file.deletions)) *
                                    100
                                  }%`,
                                  bgcolor: "error.main",
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>

                        {/* Expandable Diff View */}
                        {file.diff && (
                          <Collapse in={expandedDeployment === file.filename}>
                            <Box
                              sx={{
                                p: 2,
                                bgcolor: "action.hover",
                                borderTop: 1,
                                borderColor: "divider",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mb: 1, display: "block" }}
                              >
                                Changes in {file.filename}:
                              </Typography>
                              <Box
                                component="pre"
                                sx={{
                                  backgroundColor: "#1e1e1e",
                                  color: "#d4d4d4",
                                  p: 2,
                                  borderRadius: 1,
                                  overflow: "auto",
                                  maxHeight: "400px",
                                  fontFamily: "monospace",
                                  fontSize: "11px",
                                  lineHeight: 1.5,
                                  margin: 0,
                                  "& .diff-add": {
                                    bgcolor: "rgba(38, 166, 91, 0.15)",
                                    color: "#7ee787",
                                    display: "block",
                                  },
                                  "& .diff-remove": {
                                    bgcolor: "rgba(229, 83, 75, 0.15)",
                                    color: "#ff7b72",
                                    display: "block",
                                  },
                                }}
                              >
                                {file.diff.split("\n").map((line, i) => {
                                  if (
                                    line.startsWith("+") &&
                                    !line.startsWith("+++")
                                  ) {
                                    return (
                                      <span key={i} className="diff-add">
                                        {line}
                                        {"\n"}
                                      </span>
                                    );
                                  } else if (
                                    line.startsWith("-") &&
                                    !line.startsWith("---")
                                  ) {
                                    return (
                                      <span key={i} className="diff-remove">
                                        {line}
                                        {"\n"}
                                      </span>
                                    );
                                  } else {
                                    return line + "\n";
                                  }
                                })}
                              </Box>
                            </Box>
                          </Collapse>
                        )}
                      </React.Fragment>
                    ))}
                  </Paper>
                </Box>
              )}

              {/* Deployment Logs */}
              <Tabs
                value={0}
                sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
              >
                <Tab label="Deploy Log" />
                {deploymentLogs?.buildLog && <Tab label="Build Log" />}
                {deploymentLogs?.errorLog && <Tab label="Error Log" />}
              </Tabs>

              <Box
                component="pre"
                sx={{
                  backgroundColor: "#1e1e1e",
                  color: "#d4d4d4",
                  p: 2,
                  borderRadius: 1,
                  overflow: "auto",
                  maxHeight: "300px",
                  fontFamily: "monospace",
                  fontSize: "12px",
                  lineHeight: 1.5,
                }}
              >
                {deploymentLogs?.deployLog ||
                  deploymentLogs?.errorLog ||
                  "No logs available"}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Rollback Confirmation Dialog */}
      <Dialog
        open={rollbackDialogOpen}
        onClose={() => setRollbackDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Rollback</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will restore the previous version of your deployment.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to rollback this deployment? This will
            redeploy the code from the selected commit.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollbackDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmRollback}
            variant="contained"
            color="warning"
            startIcon={<UndoIcon />}
          >
            Confirm Rollback
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <ConfirmDialog
        open={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={confirmCancelDeployment}
        title="Cancel Deployment"
        message="Are you sure you want to cancel this deployment?"
        confirmText="Cancel Deployment"
        confirmColor="error"
      />
    </CardContent>
  );
};

export default SiteDeploymentsTab;
